'use no memo';

import type { LoginRequest, LoginResponse } from '../../types';

import { useMemo, useEffect, useReducer, useCallback } from 'react';

import axios, { endpoints } from 'src/lib/axios';

import { authRef } from './auth-ref';
import { AuthContext } from '../auth-context';
import { setSession, isValidToken } from './utils';
import { JWT_STORAGE_KEY, USER_STORAGE_KEY, REFRESH_STORAGE_KEY } from './constant';

// ----------------------------------------------------------------------

type AuthState = {
  user: { id: number; name: string; phone: string; type: string } | null;
  accessToken: string | null;
  refreshToken: string | null;
  isLoading: boolean;
};

type Action =
  | { type: 'LOGIN_START' }
  | {
      type: 'LOGIN_SUCCESS';
      payload: { user: AuthState['user']; accessToken: string; refreshToken: string };
    }
  | { type: 'LOGIN_FAILURE' }
  | { type: 'UPDATE_USER'; payload: { user: Partial<AuthState['user']> } }
  | { type: 'CLEAR_AUTH' };

const initialState: AuthState = {
  user: null,
  accessToken: null,
  refreshToken: null,
  isLoading: false,
};

function authReducer(state: AuthState, action: Action): AuthState {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, isLoading: true };
    case 'LOGIN_SUCCESS':
      return {
        user: action.payload.user,
        accessToken: action.payload.accessToken,
        refreshToken: action.payload.refreshToken,
        isLoading: false,
      };
    case 'LOGIN_FAILURE':
      return { ...state, isLoading: false };
    case 'UPDATE_USER':
      return {
        ...state,
        user: state.user ? { ...state.user, ...action.payload.user } : state.user,
      };
    case 'CLEAR_AUTH':
      return { ...initialState };
    default:
      return state;
  }
}

function persistState(
  user: AuthState['user'],
  accessToken: string,
  refreshToken: string | null
): void {
  localStorage.setItem(JWT_STORAGE_KEY, accessToken);
  if (refreshToken) {
    localStorage.setItem(REFRESH_STORAGE_KEY, refreshToken);
  } else {
    localStorage.removeItem(REFRESH_STORAGE_KEY);
  }
  localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
}

function loadPersistedAuth(): Pick<AuthState, 'user' | 'accessToken' | 'refreshToken'> | null {
  try {
    const accessToken = localStorage.getItem(JWT_STORAGE_KEY);
    const refreshToken = localStorage.getItem(REFRESH_STORAGE_KEY);
    const userRaw = localStorage.getItem(USER_STORAGE_KEY);

    if (accessToken && userRaw) {
      return { user: JSON.parse(userRaw), accessToken, refreshToken };
    }

    return null;
  } catch {
    return null;
  }
}

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const [state, dispatch] = useReducer(authReducer, initialState, () => {
    // Initialize from persisted state on first render
    const persisted = loadPersistedAuth();
    if (persisted) {
      return {
        user: persisted.user,
        accessToken: persisted.accessToken,
        refreshToken: persisted.refreshToken,
        isLoading: false,
      };
    }
    return initialState;
  });

  // Clear auth action
  const clearAuth = useCallback(() => {
    dispatch({ type: 'CLEAR_AUTH' });
    localStorage.removeItem(JWT_STORAGE_KEY);
    localStorage.removeItem(REFRESH_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
    setSession(null);
  }, []);

  // Register clearAuth on the module-level ref so axios can call it
  useEffect(() => {
    authRef.clearAuth = clearAuth;
    return () => {
      authRef.clearAuth = null;
    };
  }, [clearAuth]);

  // Persist state whenever auth data changes
  useEffect(() => {
    if (state.user && state.accessToken) {
      persistState(state.user, state.accessToken, state.refreshToken);
    }
  }, [state.user, state.accessToken, state.refreshToken]);

  const updateUser = useCallback((updatedUser: Partial<AuthState['user']>) => {
    dispatch({ type: 'UPDATE_USER', payload: { user: updatedUser } });
  }, []);

  // Login
  const login = useCallback(
    async (loginUser: LoginRequest) => {
      try {
        dispatch({ type: 'LOGIN_START' });

        const response = await axios.post(endpoints.auth.signIn, loginUser);
        const data: LoginResponse = response.data;

        // Set token in localStorage & axios headers
        setSession(data.access);

        // Persist immediately so data survives router.refresh() (full page reload)
        persistState(data.user, data.access, data.refresh);

        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: {
            user: data.user,
            accessToken: data.access,
            refreshToken: data.refresh,
          },
        });
      } catch (error) {
        dispatch({ type: 'LOGIN_FAILURE' });
        clearAuth();
        throw error;
      }
    },
    [clearAuth]
  );

  // Logout
  const logout = useCallback(() => {
    clearAuth();
  }, [clearAuth]);

  // Check user session
  const checkUserSession = useCallback(async () => {
    try {
      const storedToken = localStorage.getItem(JWT_STORAGE_KEY);

      if (storedToken && isValidToken(storedToken)) {
        if (state.user && state.accessToken === storedToken) {
          setSession(storedToken);
          return;
        }
        setSession(storedToken);
      } else {
        clearAuth();
      }
    } catch (error) {
      console.error('사용자 세션 확인 실패:', error);
      clearAuth();
    }
  }, [state.user, state.accessToken, clearAuth]);

  useEffect(() => {
    if (!state.user && !state.isLoading) {
      checkUserSession();
    }
  }, [state.user, state.isLoading, checkUserSession]);

  // Computed auth status
  const isAuthenticated = !!state.user && !!state.accessToken;

  const memoizedValue = useMemo(
    () => ({
      user: state.user ? { ...state.user } : null,
      checkUserSession,
      updateUser,
      loading: state.isLoading,
      authenticated: isAuthenticated,
      unauthenticated: !isAuthenticated,
      login,
      logout,
      clearAuth,
      getAccessToken: () => state.accessToken,
      getRefreshToken: () => state.refreshToken,
    }),
    [
      state.user,
      state.isLoading,
      state.accessToken,
      state.refreshToken,
      isAuthenticated,
      checkUserSession,
      updateUser,
      login,
      logout,
      clearAuth,
    ]
  );

  return <AuthContext value={memoizedValue}>{children}</AuthContext>;
}
